import React, { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { usePublicClient } from "wagmi";
import { reactIcons } from "../../utils/icons";
import certificateAbi from "./abi/MSTCertificateNFT.json";
import { getEventById } from "./eventsConfig";
import { decodeVerificationData, hashCertificatePayload } from "./utils/certificateUtils";

const certificateNFTAddress = "0x870B140B075d8Cf62529711D91832fdFA2741e5f";

const STATUS = {
  LOADING: "loading",
  VALID: "valid",
  TAMPERED: "tampered",
  NOT_FOUND: "not_found",
  BAD_LINK: "bad_link"
};

const CertificateVerify = () => {
  const { certificateId } = useParams();
  const [searchParams] = useSearchParams();
  const publicClient = usePublicClient();
  const [status, setStatus] = useState(STATUS.LOADING);
  const [onChain, setOnChain] = useState(null);

  const encodedData = searchParams.get("d");
  const payload = encodedData ? decodeVerificationData(encodedData) : null;
  const event = payload ? getEventById(payload.e) : null;

  useEffect(() => {
    const verify = async () => {
      if (!payload || !certificateId) {
        setStatus(STATUS.BAD_LINK);
        return;
      }
      try {
        const tokenId = await publicClient.readContract({
          address: certificateNFTAddress,
          abi: certificateAbi,
          functionName: "tokenIdByCertificateId",
          args: [certificateId]
        });

        if (!tokenId || Number(tokenId) === 0) {
          setStatus(STATUS.NOT_FOUND);
          return;
        }

        const [dataHash, eventId, certId, issueDate, mintedTo] = await publicClient.readContract({
          address: certificateNFTAddress,
          abi: certificateAbi,
          functionName: "getCertificate",
          args: [tokenId]
        });

        const recomputedHash = hashCertificatePayload(payload);
        const isValid = recomputedHash?.toLowerCase() === dataHash?.toLowerCase();

        setOnChain({
          tokenId: Number(tokenId),
          eventId,
          certificateId: certId,
          issueDate: new Date(Number(issueDate) * 1000),
          mintedTo
        });
        setStatus(isValid ? STATUS.VALID : STATUS.TAMPERED);
      } catch (err) {
        setStatus(STATUS.NOT_FOUND);
      }
    };
    verify();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [certificateId, encodedData, publicClient]);

  const explorerBase = "https://mstscan.com";

  return (
    <div className="text-white px-4 pt-32 pb-10 md:pt-40 md:pb-16 max-w-3xl mx-auto">
      <div className="text-center mb-10">
        <h1 className="text-3xl md:text-4xl font-bold mb-3">Certificate Verification</h1>
        <p className="text-white/60 text-sm">MST Blockchain Certificate Registry</p>
      </div>

      <div className="rounded-2xl border border-white/10 bg-[#141416] p-6 md:p-10 shadow-xl">
        {status === STATUS.LOADING && (
          <div className="flex flex-col items-center gap-4 py-16">
            <div className="h-10 w-10 border-4 border-white/20 border-t-red-600 rounded-full animate-spin" />
            <p className="text-white/70">Verifying certificate on-chain...</p>
          </div>
        )}

        {status === STATUS.BAD_LINK && (
          <div className="text-center py-16">
            <div className="text-5xl mb-4 text-red-500">✕</div>
            <h2 className="text-2xl font-bold mb-2">Invalid Verification Link</h2>
            <p className="text-white/60">This link is missing or has corrupted certificate data.</p>
          </div>
        )}

        {status === STATUS.NOT_FOUND && (
          <div className="text-center py-16">
            <div className="text-5xl mb-4 text-red-500">✕</div>
            <h2 className="text-2xl font-bold mb-2">Certificate Not Found</h2>
            <p className="text-white/60">
              No certificate with ID <span className="font-mono">{certificateId}</span> exists on-chain.
            </p>
          </div>
        )}

        {status === STATUS.TAMPERED && (
          <div className="text-center py-10">
            <div className="text-5xl mb-4 text-red-500">✕</div>
            <h2 className="text-2xl font-bold mb-2">Certificate Data Mismatch</h2>
            <p className="text-white/60">
              The certificate data in this link does not match what is recorded on-chain. This
              certificate link may have been tampered with.
            </p>
          </div>
        )}

        {status === STATUS.VALID && onChain && (
          <div className="flex flex-col gap-6">
            <div className="flex items-center gap-2 text-green-400 font-semibold">
              <span className="text-2xl">{reactIcons.badgeCheck}</span>
              Certificate Verified — Authentic
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <Field label="Participant Name" value={payload?.n} />
              <Field label="Event Name" value={payload?.en || event?.name} />
              <Field label="Venue" value={payload?.v || event?.venue} />
              <Field label="Organizer" value={payload?.o || event?.organizer} />
              <Field label="Certificate ID" value={onChain.certificateId} mono />
              <Field label="NFT Token ID" value={onChain.tokenId} mono />
              <Field label="Wallet Address" value={onChain.mintedTo} mono />
              <Field
                label="Issue Date"
                value={onChain.issueDate.toLocaleDateString("en-GB", {
                  day: "2-digit",
                  month: "long",
                  year: "numeric"
                })}
              />
              <Field label="Smart Contract Address" value={certificateNFTAddress} mono full />
            </div>

            {explorerBase && (
              <a
                href={`${explorerBase}/address/${certificateNFTAddress}`}
                target="_blank"
                rel="noreferrer"
                className="flex items-center justify-center gap-2 px-6 py-3 rounded-full border border-red-600 button-gradient-bg text-white font-semibold shadow-md transition self-start"
              >
                View on MSTScan {reactIcons.walletArrow}
              </a>
            )}
          </div>
        )}

        <div className="mt-10 pt-6 border-t border-white/10 text-center">
          <Link to="/nft-minting" className="text-white/50 hover:text-white text-sm">
            Mint your own certificate on MST Blockchain →
          </Link>
        </div>
      </div>
    </div>
  );
};

const Field = ({ label, value, mono, full }) => (
  <div className={`rounded-lg bg-[#0c0c0c] border border-white/10 px-4 py-3 ${full ? "md:col-span-2" : ""}`}>
    <div className="text-white/50 mb-1">{label}</div>
    <div className={`break-all ${mono ? "font-mono" : ""}`}>{value || "—"}</div>
  </div>
);

export default CertificateVerify;
